'use client';

import {
  ListFilter,
  Lock,
  type LucideIcon,
  Menu,
  PanelLeftClose,
  Search,
  Sparkles,
  SquarePen,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  type RefObject,
  useEffect,
  useState,
} from 'react';

import { ThemedUserButton } from '@/components/auth/ThemedUserButton';
import { SidebarMenuContent } from '@/components/layout/SidebarMenuContent';
import CrownButton from '@/components/marketing/CrownButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Link, usePathname } from '@/i18n/routing';
import Logo from '@/lib/logo/Logo';
import { cn } from '@/lib/utils';

import { useAuthUser } from '@/hooks/useAuthUser';
import { toVisibleGroups, useSidebarLayout } from '@/hooks/useSidebarLayout';
import { MIN_PANE_PX, useSidebarSplit } from '@/hooks/useSidebarSplit';
import { useAppDispatch } from '@/lib/redux/hooks';
import { setCustomizeSheetOpen } from '@/lib/redux/slices/uiSettingsSlice';
import { AdvocateClaimBanner } from './AdvocateClaimBanner';
import { SidebarSplitHandle } from './SidebarSplitHandle';
import {
  type SidebarSection,
  type SidebarSectionKey,
  getActiveSection,
} from './sidebarSections';

interface SidebarRailProps {
  // True for the simplified anonymous sidebar (auth-only sections hidden).
  isAnonymousMode?: boolean;
  // Hide the premium upsell crown when the user already has a paid plan.
  isPremiumActive?: boolean;
  // Collapses the whole sidebar to the 56px icon rail (desktop). Undefined on
  // mobile, where the drawer is dismissed by its backdrop / nav taps instead.
  onCollapse?: (() => void) | undefined;
  // Fired when the user picks a nav destination — used on mobile to close the
  // drawer once navigation starts.
  onNavigate?: (() => void) | undefined;
  // Opens the ⌘K search palette. Omitted where no palette exists (anonymous
  // users have no chat history) so we never show a dead button.
  onSearchChats?: (() => void) | undefined;
  // The chat-history ("Recents") list rendered below the nav. Owns its own
  // scroll so a long, virtualized list keeps a bounded height. Undefined for
  // anonymous users (no history).
  recents?: ReactNode;
  // Current Recents grouping ('date' = Today/Yesterday/… headers, 'none' =
  // flat). Drives the check-mark in the group-by control.
  recentsGroupBy?: 'date' | 'none';
  // Changes the Recents grouping. When provided (with a recents list), the
  // "Group by" control renders in the Recents header; omitted ⇒ no control.
  onRecentsGroupByChange?: ((groupBy: 'date' | 'none') => void) | undefined;
  // Whether the menu/Recents split may be dragged. False on mobile, where the
  // drawer has no pointer and an ~9px drag strip would sit below the 44px touch
  // floor (WCAG 2.1 AA SC 2.5.5) — see SidebarSplitHandle. The 50/50 sizing
  // still applies there; only the handle is withheld.
  canResizeSplit?: boolean;
  // Usage meter footer (authenticated users) — rendered above the account row.
  usageSlot?: ReactNode;
  // Login footer for anonymous users — replaces the authenticated footer.
  anonFooterSlot?: ReactNode;
}

// ── Class tokens ──
// Every nav / footer row shares the 44px floor (WCAG 2.1 AA SC 2.5.5) via a
// bare-identifier const so the SidebarTouchTargets fence can resolve the literal
// (`className={ident}`). Rows are a `group` so the icon lifts from the neutral
// resting tone to `content-primary` on hover (see `navIconClass`).
const rowClass =
  'group relative flex w-full items-center gap-2.5 rounded-md min-h-[44px] px-2 text-sm transition-colors cursor-pointer';
const disabledRowClass =
  'relative flex w-full items-center gap-2.5 rounded-md min-h-[44px] px-2 text-sm opacity-60 cursor-default';
// Same shape as rowClass, for a button row with no "active" state (e.g.
// Customize) so it always shows the hover background. Kept as its own pure
// literal (not `cn(rowClass, 'hover:bg-interactive-hover')`) so the
// SidebarTouchTargets generic-button fence — which can't evaluate a cn() call
// that mixes a bare identifier with an inline string literal — can still
// resolve the 44px floor via a plain bare-identifier lookup.
const rowClassAlwaysHover =
  'group relative flex w-full items-center gap-2.5 rounded-md min-h-[44px] px-2 text-sm transition-colors cursor-pointer hover:bg-interactive-hover';
// Footer rows (account / menu / customize, plus the optional Crown upsell) —
// pinned by the touch-target fence via this exact trio (hover:bg-surface-muted
// + w-full + min-h-[44px]).
const footerRowClass =
  'group relative flex w-full items-center gap-2.5 rounded-md min-h-[44px] px-2 text-sm transition-colors cursor-pointer hover:bg-surface-muted';
// Header icon buttons (search / collapse) — icon-only ≥44px squares.
const headerIconBtnClass =
  'flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-brand-action-muted hover:text-brand-tertiary hover:bg-interactive-hover transition-colors cursor-pointer';

// Fixed icon box keeps every row's glyph aligned in a single left column.
const iconBox = 'flex h-6 w-6 shrink-0 items-center justify-center';

// Resting icon tone for the whole column: a calm neutral that lifts to
// `content-primary` on row hover (each row is a `group`). The brand accent is
// reserved for the active row alone (`activeIconClass`), so "you are here" stays
// legible. The claude.com / Linear / Notion convention.
const navIconClass =
  'text-brand-action-muted transition-colors group-hover:text-brand-tertiary';

// The single "you are here" accent — `brand-tertiary-active`, a per-theme token
// tuned to clear WCAG 1.4.11 3:1 on the selected-row bg (#6119 / #6127). Used
// for the active nav row.
const activeIconClass = 'text-brand-tertiary-active';

// Compact group-label style (claude.ai pattern): tiny uppercase tracked text
// that sits above each labeled cluster (Tools / Documents / Professionals /
// Practice). Matches the Recents header so the column reads as one rhythm.
const groupHeaderClass =
  'px-2 pt-1 pb-0.5 text-[11px] font-medium uppercase tracking-wide text-content-muted';

// ── Nav-row appearance ──
// One decision each, at module scope, as early returns rather than nested
// ternaries. That is why they live outside the component: `renderSection`
// inlined all of them as stacked conditionals, which is most of what put it at
// cognitive complexity 28 (max 10). Naming them also documents a precedence
// that the nested form stated only by accident of ordering — DISABLED WINS
// OVER ACTIVE, in both tint and tone.

/** Badge caption for a nav row, or null when the section carries no badge. */
const badgeCaption = (
  badge: SidebarSection['badge'],
  tCommon: (key: string) => string
): string | null => {
  if (!badge) return null;
  if (badge === 'beta') return tCommon('beta');
  if (badge === 'locked') return tCommon('locked');
  return tCommon('comingSoon');
};

/** Icon tint for a nav row. */
const navIconTint = (disabled: boolean, active: boolean): string => {
  if (disabled) return 'text-content-muted';
  return active ? activeIconClass : navIconClass;
};

/** Label typography for a nav row. */
const navLabelTone = (disabled: boolean, active: boolean): string => {
  if (disabled) return 'text-content-muted';
  if (active) return 'font-medium text-content-primary';
  return 'text-content-secondary group-hover:text-content-primary';
};

/**
 * Whether a nav row is the current destination.
 *
 * The bespoke "New chat" row owns the new-chat landing ('/' and '/chat'). The
 * Chats/Konverzácie row is also keyed to the 'chat' section, so without the
 * second clause both rows light up at once on the landing (getActiveSection
 * returns 'chat' there for the recents fallback). Konverzácie stays active on
 * the chats list ('/chats') and on open sessions ('/chat/:id').
 */
const isSectionActive = (
  activeSection: SidebarSectionKey | null,
  section: SidebarSection,
  isNewChatActive: boolean
): boolean =>
  activeSection === section.key && !(section.key === 'chat' && isNewChatActive);

/** Accessible name for a nav row — the badge is spoken after the label. */
const rowAccessibleName = (label: string, badgeText: string | null): string =>
  badgeText ? `${label}, ${badgeText}` : label;

/** Row chrome for an ENABLED nav row (the disabled branch has its own class). */
const navRowClass = (active: boolean): string =>
  cn(
    rowClass,
    active ? 'bg-interactive-selected' : 'hover:bg-interactive-hover'
  );

// ── Nav pane sizing ──
// The nav block is the split's TOP PANE only when there is a Recents list to
// split against (see useSidebarSplit). In anon mode there is none, so it keeps
// the older content-sized-but-shrinkable behaviour and the flex-1 spacer below
// pins the footer. Both live at module scope for the same reason the nav-row
// helpers do: their conditionals would otherwise be charged to the component's
// already-oversized render.

/** Class list for the nav pane. */
const menuPaneClass = (hasRecents: boolean): string =>
  cn('min-h-0 space-y-0.5 overflow-y-auto px-2', !hasRecents && 'shrink');

/**
 * Ref + flex sizing for the nav pane, or nothing when there is no split.
 * The ref is what the drag measures from, so it must be attached exactly when
 * the pane is a split participant — attaching it in anon mode would give
 * useSidebarSplit a pane whose sibling never renders.
 */
const menuPaneSplitProps = (
  hasRecents: boolean,
  menuRef: RefObject<HTMLDivElement | null>,
  menuRatio: number
): { ref?: RefObject<HTMLDivElement | null>; style?: CSSProperties } =>
  hasRecents
    ? { ref: menuRef, style: { flexGrow: menuRatio, flexBasis: 0 } }
    : {};

/**
 * The inside of a nav row — accent bar, icon, label, optional badge.
 *
 * Shared verbatim by the enabled (<Link>) and disabled (<button>) branches so
 * the two can never drift apart, and kept at module scope so its own JSX
 * conditionals are not charged to `renderSection`. The badge arrives already
 * rendered, because building it needs the component's translator.
 */
const NavRowBody = ({
  active,
  icon: Icon,
  iconColor,
  disabled,
  label,
  badgeNode,
}: {
  active: boolean;
  icon: LucideIcon;
  iconColor: string;
  disabled: boolean;
  label: string;
  badgeNode: ReactNode;
}) => (
  <>
    {/* Active accent bar — an unambiguous "you are here" marker. Same
        `brand-tertiary-active` token as the icon so they can never mismatch. */}
    {active && (
      <span
        aria-hidden='true'
        className='absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-tertiary-active'
      />
    )}
    <span className={iconBox}>
      <Icon size={18} className={iconColor} />
    </span>
    <span
      className={cn(
        'min-w-0 flex-1 truncate text-left',
        navLabelTone(disabled, active)
      )}
    >
      {label}
    </span>
    {badgeNode}
  </>
);

export const SidebarRail = ({
  isAnonymousMode = false,
  isPremiumActive = false,
  onCollapse,
  onNavigate,
  onSearchChats,
  recents,
  recentsGroupBy = 'date',
  onRecentsGroupByChange,
  canResizeSplit = true,
  usageSlot,
  anonFooterSlot,
}: SidebarRailProps) => {
  const t = useTranslations('SidebarHeader');
  // Draggable 50/50 split between the nav block and the Recents list.
  const {
    menuRatio,
    menuRef,
    recentsRef,
    isDragging: isSplitDragging,
    onSeparatorMouseDown,
    onSeparatorKeyDown,
    resetSplit,
  } = useSidebarSplit();
  const tCommon = useTranslations('Common');
  const tMarketing = useTranslations('marketing');
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const activeSection = getActiveSection(pathname);
  // Platform-aware ⌘K / Ctrl K hint shown in the Search tooltip. Resolved
  // client-side (navigator.userAgent — the repo's platform-detection idiom) to
  // avoid an SSR hydration mismatch; defaults to "Ctrl K".
  const [shortcutHint, setShortcutHint] = useState('Ctrl K');
  useEffect(() => {
    if (
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
    ) {
      setShortcutHint('⌘K');
    }
  }, []);

  const { practiceEnabled, planCapabilities, user, supabaseUser } =
    useAuthUser();
  const { groups } = useSidebarLayout({
    isAnonymousMode,
    practiceEnabled,
    capabilities: planCapabilities,
    // No loaded backend user → the plan can't be judged (#11227's gate rule).
    capabilitiesUnknown: supabaseUser == null,
  });
  // Drops hidden items and any group left empty as a result — the same
  // filtering rule SidebarActionList applies, so the two rails can't drift.
  const visibleGroups = toVisibleGroups(groups);
  // The 'top' cluster is special-cased positionally — it owns the bespoke
  // New-chat Link, which sits above its (customizable) item list. It renders
  // unlabeled unless the user has explicitly renamed it (see
  // useSidebarLayout's editorLabel/label distinction).
  const topGroup = visibleGroups.find((g) => g.id === 'top');
  const otherGroups = visibleGroups.filter((g) => g.id !== 'top');

  // New chat lights up only on the chat landing ('/' or '/chat'); on an open
  // session the recents list highlights that session instead.
  const isNewChatActive = pathname === '/' || pathname === '/chat';

  // A small text pill on the right of a labeled row (beta / coming-soon /
  // locked). Shape + colour both carry the signal so it survives for
  // colour-blind users. 'locked' (#7198) additionally carries a lock glyph —
  // the row's OWN icon stays the section's normal icon (e.g. FilePen for
  // "Generate document") so the entry stays recognizable; the lock lives only
  // in this badge. Uses the `premium` token, not `status-warning`: this is an
  // upsell affordance ("unlock this paid feature"), not an alert, and
  // `premium` is the token this codebase already reserves for exactly that
  // ("PREMIUM COLOR" in tailwind.config.ts, used by ProfileHero/
  // ProfessionalCard/DashboardHero's paid-tier badges) — `status-warning` is
  // the alerting palette (blacklisted email, archived listing, maintenance
  // mode), a different semantic than "you can buy this".
  const badgePill = (badge: NonNullable<SidebarSection['badge']>) => {
    const label = badgeCaption(badge, tCommon) ?? '';
    if (badge === 'locked') {
      return (
        <span className='ml-auto flex shrink-0 items-center gap-1 rounded-full border border-premium/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-premium'>
          <Lock size={9} aria-hidden='true' />
          {label}
        </span>
      );
    }
    return (
      <span
        className={cn(
          'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
          badge === 'beta'
            ? 'bg-brand-tertiary/15 text-brand-tertiary'
            : 'border border-status-info/40 text-status-info'
        )}
      >
        {label}
      </span>
    );
  };

  const renderSection = (section: SidebarSection) => {
    const active = isSectionActive(activeSection, section, isNewChatActive);
    const Icon: LucideIcon = section.icon;
    const label = t(section.labelKey as Parameters<typeof t>[0]);
    const disabled = Boolean(section.disabled);
    const badgeText = badgeCaption(section.badge, tCommon);
    const accessibleName = rowAccessibleName(label, badgeText);
    const iconColor = navIconTint(disabled, active);

    const body = (
      <NavRowBody
        active={active}
        icon={Icon}
        iconColor={iconColor}
        disabled={disabled}
        label={label}
        badgeNode={section.badge ? badgePill(section.badge) : null}
      />
    );

    // Disabled (e.g. "coming soon") — a focusable, aria-disabled button so
    // keyboard and screen-reader users discover it exists.
    if (disabled || !section.href) {
      return (
        <button
          type='button'
          aria-disabled='true'
          aria-label={accessibleName}
          onClick={(e) => e.preventDefault()}
          className={disabledRowClass}
        >
          {body}
        </button>
      );
    }

    return (
      <Link
        href={section.href as never}
        onClick={() => onNavigate?.()}
        aria-label={accessibleName}
        aria-current={active ? 'page' : undefined}
        className={navRowClass(active)}
      >
        {body}
      </Link>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <nav
        aria-label={tCommon('appNavigation')}
        className='flex h-full w-full flex-col overflow-hidden bg-surface-sidebar'
      >
        {/* Header: brand + search + collapse */}
        <div className='flex items-center justify-between gap-1 px-2 pb-0.5 pt-1.5'>
          <Link
            href='/'
            aria-label='Lexomat'
            onClick={() => onNavigate?.()}
            className='flex min-h-[44px] items-center rounded-md px-1'
          >
            <Logo showText />
          </Link>
          <div className='flex items-center gap-0.5'>
            {onSearchChats && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    onClick={() => {
                      onNavigate?.();
                      onSearchChats();
                    }}
                    aria-label={t('search')}
                    aria-haspopup='dialog'
                    aria-describedby={undefined}
                    className={headerIconBtnClass}
                  >
                    <Search size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  <span className='flex items-center gap-2'>
                    <span>{t('search')}</span>
                    {/* SR separator → ", " so the announcement reads
                        "Search, ⌘K", not the run-on "Search⌘K" (#6140). */}
                    <span className='sr-only'>, </span>
                    <kbd className='rounded border border-line-default px-1 text-[10px] font-medium text-content-muted'>
                      {shortcutHint}
                    </kbd>
                  </span>
                </TooltipContent>
              </Tooltip>
            )}
            {onCollapse && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    onClick={onCollapse}
                    aria-label={tCommon('closeSidebar')}
                    aria-describedby={undefined}
                    className={headerIconBtnClass}
                  >
                    <PanelLeftClose size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  {tCommon('closeSidebar')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Nav items. With Recents present this is the TOP PANE of the
            draggable split: it takes `menuRatio` of the space the two panes
            share (an even half by default) and scrolls internally. Without
            Recents (anon mode) there is nothing to split against, so it keeps
            the old content-sized-but-shrinkable behaviour and the spacer below
            pins the footer. */}
        <div
          className={menuPaneClass(Boolean(recents))}
          {...menuPaneSplitProps(Boolean(recents), menuRef, menuRatio)}
        >
          {/* Top (unlabeled) cluster: New chat + Konverzácie. */}
          <Link
            href='/chat'
            aria-label={t('newChat')}
            aria-current={isNewChatActive ? 'page' : undefined}
            onClick={() => onNavigate?.()}
            className={cn(
              rowClass,
              isNewChatActive
                ? 'bg-interactive-selected'
                : 'hover:bg-interactive-hover'
            )}
          >
            <span className={iconBox}>
              <SquarePen
                size={18}
                className={isNewChatActive ? activeIconClass : navIconClass}
              />
            </span>
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-left',
                isNewChatActive
                  ? 'font-medium text-content-primary'
                  : 'text-content-secondary group-hover:text-content-primary'
              )}
            >
              {t('newChat')}
            </span>
          </Link>

          {/* Top cluster's customizable items (Konverzácie by default, plus
              anything the user has dragged into 'top') — same row shape,
              sits alongside New chat. Renders a header only if the user has
              explicitly renamed 'top' (see useSidebarLayout). */}
          {topGroup?.label && (
            <div aria-hidden='true' className={groupHeaderClass}>
              {topGroup.label}
            </div>
          )}
          {(topGroup?.items ?? []).map((section) => (
            <Fragment key={section.key}>{renderSection(section)}</Fragment>
          ))}

          {/* Customize — a lightweight quick-access shortcut in the primary
              nav, right below Chats. #5227 had moved Customize out of primary
              nav entirely into the account/menu popover footer ("a settings
              concern, not a nav destination"); reinstated here as a shortcut
              because it was too easy to miss inside the footer menu. The full
              entry still lives in the footer menu (SidebarMenuContent) —
              this is a second, quicker entry point to the same
              CustomizeSheet, not a replacement. */}
          <button
            type='button'
            aria-label={t('customize')}
            onClick={() => dispatch(setCustomizeSheetOpen(true))}
            className={rowClassAlwaysHover}
          >
            <span className={iconBox}>
              <Sparkles size={18} className={navIconClass} />
            </span>
            <span className='min-w-0 flex-1 truncate text-left text-content-secondary group-hover:text-content-primary'>
              {t('customize')}
            </span>
          </button>

          {/* Labeled groups — one header per cluster, in the user's
              customized order (built-in groups + any custom groups they've
              created), only when the cluster actually has at least one
              surviving item. The header's small top padding opens a little
              vertical space before each cluster, matching the visual rhythm
              the Recents header sets. */}
          {otherGroups.map((group) => (
            <Fragment key={group.id}>
              {group.label && (
                <div aria-hidden='true' className={groupHeaderClass}>
                  {group.label}
                </div>
              )}
              {group.items.map((section) => (
                <Fragment key={section.key}>{renderSection(section)}</Fragment>
              ))}
            </Fragment>
          ))}

          {/* Advocate claim banner — shown when user has selected the Advokát
              profile type (practice_self_enabled) but hasn't claimed a registry
              profile yet (isProfessional=false). Links to /advokati to start the
              claim flow. Dismissed automatically once the profile is claimed. */}
          {!isAnonymousMode &&
            supabaseUser?.practice_self_enabled &&
            !user?.isProfessional && <AdvocateClaimBanner />}
        </div>

        {/* Recents — takes all remaining vertical space and clips the scroll
            container to a bounded height. Absent (anon mode) → flex-1 spacer
            keeps the footer pinned at the bottom of the full-height nav. */}
        {recents ? (
          <>
            <SidebarSplitHandle
              canResize={canResizeSplit}
              menuRatio={menuRatio}
              isDragging={isSplitDragging}
              label={t('resizeSplit')}
              onMouseDown={onSeparatorMouseDown}
              onKeyDown={onSeparatorKeyDown}
              onReset={resetSplit}
            />
            {/* Recents header: a visual section label (not a heading — kept
                out of the document outline) + the "Group by" control. */}
            <div className='flex shrink-0 items-center justify-between gap-1 px-3'>
              <span className='text-xs font-medium uppercase tracking-wide text-content-muted'>
                {t('recents')}
              </span>
              {onRecentsGroupByChange && (
                // DropdownMenu (not a hand-rolled Popover) so the menu gets the
                // full ARIA menu pattern for free: arrow-key navigation,
                // auto-close on select, a labelled container, and the radio
                // indicator — all managed by Radix.
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button
                          type='button'
                          aria-label={t('groupBy')}
                          aria-describedby={undefined}
                          className={headerIconBtnClass}
                        >
                          <ListFilter size={16} />
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side='left'>{t('groupBy')}</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align='end' className='w-44'>
                    <DropdownMenuLabel className='text-xs font-medium uppercase tracking-wide text-content-muted'>
                      {t('groupBy')}
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={recentsGroupBy}
                      onValueChange={(v) =>
                        onRecentsGroupByChange(v as 'date' | 'none')
                      }
                    >
                      <DropdownMenuRadioItem
                        value='none'
                        className='min-h-[44px] cursor-pointer'
                      >
                        {t('groupByNone')}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem
                        value='date'
                        className='min-h-[44px] cursor-pointer'
                      >
                        {t('groupByDate')}
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {/* `recents` is the scroll container (it owns the infinite-scroll
                listener + ref), so this wrapper is just a flex column that
                bounds the height; the child fills it via flex-1 (no `h-full`
                percentage on a flex item — fragile in Firefox). */}
            {/* Bottom pane of the split — takes the remainder of `menuRatio`.
                The min-height floor matches the one the drag clamps to, so a
                viewport too short to honour both floors squeezes the nav (which
                scrolls) rather than collapsing the history list to nothing —
                the same priority the pre-split layout had. */}
            <div
              ref={recentsRef}
              className='flex min-h-0 flex-col overflow-hidden px-1'
              style={{
                flexGrow: 1 - menuRatio,
                flexBasis: 0,
                minHeight: MIN_PANE_PX,
              }}
            >
              {recents}
            </div>
          </>
        ) : (
          <div className='flex-1' />
        )}

        {/* Auth footer: combined account/menu/crown row, or the anon footer slot. */}
        {isAnonymousMode ? (
          anonFooterSlot
        ) : (
          <div className='border-t border-line-subtle'>
            {usageSlot}
            <div className='px-2 py-0.5'>
              {/* Combined footer row: avatar + display name + plan on the left
                  open the account dropdown (UserMenuContent — usage /
                  settings / sign-out); the Menu and Crown icon buttons on the
                  right carry the footer-links popover and the Upgrade CTA.
                  Customize lives in the shared footer menu
                  (SidebarMenuContent). One trigger, one menu — the avatar
                  previously opened the account dropdown AND the footer-links
                  popover stacked on the same click. */}
              <div className={footerRowClass}>
                <ThemedUserButton userProfileUrl='/user-profile'>
                  <button
                    type='button'
                    aria-label={t('account')}
                    className='flex min-h-[44px] min-w-0 flex-1 items-center gap-2.5 rounded-md py-1 pl-1 pr-2 text-left transition-colors hover:bg-surface-muted/60 cursor-pointer'
                  >
                    {/* Avatar/initials glyph — uses the same iconBox shape
                        so the row stays vertically centered. */}
                    <span className={iconBox}>
                      <span
                        aria-hidden='true'
                        className='flex h-6 w-6 items-center justify-center rounded-full bg-brand-tertiary/15 text-xs font-semibold uppercase text-brand-tertiary'
                      >
                        {(
                          user?.fullName?.charAt(0) ||
                          user?.email?.charAt(0) ||
                          '?'
                        ).toUpperCase()}
                      </span>
                    </span>
                    <span className='min-w-0 flex-1 truncate text-left'>
                      <span className='block truncate text-sm font-medium text-content-primary'>
                        {user?.fullName || user?.email || t('account')}
                      </span>
                      {user?.plan && (
                        <span className='block truncate text-xs text-content-muted'>
                          {user.plan}
                        </span>
                      )}
                    </span>
                  </button>
                </ThemedUserButton>

                <div className='flex shrink-0 items-center gap-1'>
                  {/* Menu — opens the footer popover (SidebarMenuContent:
                      customize, marketing/legal links, feedback, cookies).
                      Tooltip so the icon-only affordance isn't hidden
                      knowledge. Nesting order (Popover > Tooltip >
                      TooltipTrigger > PopoverTrigger > button) matches the
                      collapsed rail in MainContentArea — both Radix triggers
                      merge onto the one button. */}
                  <Popover>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <button
                            type='button'
                            aria-label={tCommon('openFooterMenu')}
                            className={headerIconBtnClass}
                          >
                            <Menu size={18} />
                          </button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent side='top'>
                        {tCommon('menu')}
                      </TooltipContent>
                    </Tooltip>
                    <PopoverContent
                      className='w-max p-2'
                      align='start'
                      side='top'
                    >
                      <SidebarMenuContent showCookiePreferences showCustomize />
                    </PopoverContent>
                  </Popover>

                  {/* Crown upsell — only when the user doesn't already have a
                      paid plan (matches the prior isPremiumActive gate). The
                      tooltip on the icon button stays as `upgradeToPremium`
                      so the marketing CTA survives in the rail. */}
                  {!isPremiumActive && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className='px-0.5'
                          title={tMarketing('upgradeToPremium')}
                        >
                          <CrownButton />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side='top'>
                        {tMarketing('upgradeToPremium')}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* Hidden trigger preserved for the design system — the legacy
                  footer used a "collapse account zone" affordance via a
                  chevron-up icon. Kept as an aria-hidden chevron in the same
                  row so the visual rhythm matches the reference layout; the
                  click behaviour is owned by the avatar/menu above. */}
            </div>
          </div>
        )}
      </nav>
    </TooltipProvider>
  );
};
